import { Spacing } from '@persona-id/design-tokens';
import { EyeOffIcon } from '@persona-id/design-tokens/icons';
import produce from 'immer';
import { get, omit, uniq } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useDispatch, useSelector } from 'react-redux';
import { SPACINGS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import { moduleOutlineMixin } from 'dashboard/apps/DashboardLayoutEditor/LayoutPreview/layers/shared';
import { findComponentConfigPath } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import { NONDRAGGABLE_COMPONENTS } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-constants';
import { UiHideableComponent } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/types';
import {
  isDisableableComponent,
  isDisabledComponent,
  isDisabledComponentConfig,
  isHiddenComponentConfig,
  isHideableComponent,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/utils';
import {
  baseContentEditableStyles,
  InlineTextEditor,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/InlineTextEditor';
import { shouldEnableInlineTextEdits } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/InlineTextEditor/utils';
import {
  DraggableHoverPosition,
  isComposedUiComponentConfig,
  ScreenEditorComponentItem,
  ScreenEditorDraggableItemTypes,
  SelectedComponentAndStep,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import {
  addComponentsAtIndex,
  createComponentConfigAndFields,
  determineComponentShiftIndex,
  getComponentDisplayName,
  isComponentFromStep,
  isCurrentComponentBIPASubmitTrigger,
  isCurrentComponentBIPAText,
  isShiftingComponentUp,
  MockInquiryPropsForUiComponent,
  removeComponentFromPath,
  restrictNestedComponentDrop,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/utils';
import { generateUpdatedStepFromComponentUpdates } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import StalledAnimationContainer from 'dashboard/apps/InquiryTemplateEditor/StalledAnimationContainer';
import { handleTranslationUpdate } from 'dashboard/apps/InquiryTemplateEditor/TranslationInputs/TranslationInputs';
import NodeSticker from 'dashboard/components/NodeSticker';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import {
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
} from 'dashboard/models/inquiry-template-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { refreshComponentPreview } from 'dashboard/redux/thunk-actions/inquiry-template';
import { StackChild, StackContainer } from 'inquiry/components/StackBase';
import UiComponentView from 'inquiry/components/UiComponentView';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { FieldSchema } from 'lib/fields';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useToggle from 'lib/hooks/useToggle';
import {
  sizeCss,
  Style,
  StyleKey,
  StyleValueFramePositioning,
  StyleValueSize,
} from 'lib/inquiryStyles';
import { StepPreview } from 'lib/interfaces/StepsConfig';
import {
  componentFlexToHorizontalAxis,
  HorizontalStackComponent,
  isImageComponent,
  isTextComponentConfig,
  isUiButtonComponent,
  isUiParentComponent,
  isUiParentComponentConfig,
  ParentLayoutComponent,
  StackComponentStyles,
  UiComponent,
  UiComponentConfig,
  UiComponentConfigType,
  UiComponentType,
  UiParentComponent,
  UiParentComponentConfig,
  UiTextComponent,
  UiTextComponentConfig,
} from 'lib/interfaces/ui-components';

const MIN_WIDTH_COMPONENTS = [UiComponentType.PersonaBranding, UiComponentType.LanguageSelector];

const determineStackContainerSidePadding = (sizeUnit?: StyleValueSize) => {
  return sizeUnit == null || sizeUnit.value === 0 ? Spacing[1] : sizeCss(sizeUnit);
};

const determineWidthForComponent = (styles?: UiComponent['styles']) => {
  return styles != null && 'width' in styles
    ? sizeCss((styles.width as Style<StyleKey.Width>).base.base) || '100%'
    : '100%';
};

const isDraggedComponentFooter = (type: UiComponentConfigType) =>
  type === UiComponentConfigType.Footer;

const ImageWrapper = styled.div<{
  alignment?: string;
  width?: string;
}>`
  align-self: ${({ alignment }) => alignment ?? 'start'};
  width: ${({ width }) => width ?? '100%'};
  max-width: 100%;
`;

const ScreenComponentWrapper = styled.div<{
  isHovering: boolean;
  isSelected: boolean;
  isErrorSelected: boolean;
  isOver: boolean;
  isOverCurrent: boolean;
  hoverPosition?: DraggableHoverPosition;
  isParentComponent?: boolean;
  parentComponent?: UiParentComponent;
  parentFramePositioning?: StyleValueFramePositioning;
  component: UiComponent;
}>`
  ${moduleOutlineMixin}
  outline-offset: 0px;
  z-index: 1;
  outline-width: 1px;
  display: flex;
  flex-direction: column;
  width: ${({ component }) =>
    !MIN_WIDTH_COMPONENTS.includes(component.type)
      ? determineWidthForComponent(component.styles)
      : 'min-content'};
  align-self: ${({ component }) =>
    component.styles != null && 'justify' in component.styles
      ? (component.styles.justify as Style<StyleKey.Justify>).base.base
      : 'start'};
  max-width: 100%;
  position: relative;

  // update alignment of components within footer based on Footer component
  // justify setting
  ${({ parentComponent }) =>
    parentComponent?.type === UiComponentType.Footer &&
    css`
      align-items: ${parentComponent.styles?.justify?.base?.base ?? 'center'};
    `}

  // edge-to-edge override for margins and width
  ${({ parentFramePositioning }) =>
    parentFramePositioning === StyleValueFramePositioning.EdgeToEdge &&
    css`
      margin-left: -${SPACINGS.widgetPadding};
      margin-right: -${SPACINGS.widgetPadding};

      // accounts for screen width + padding so should always count
      // and not have a max-width overrida
      width: ${`calc(100% - -${SPACINGS.widgetPadding} - -${SPACINGS.widgetPadding})`};
      max-width: none;

      // have children respect the frame-positioning setting on the parent
      ${StackContainer} {
        margin-left: 0;
        margin-right: 0;
        width: 100%;
      }
    `}

  /* hover indicator for inserting components in-between and hover over parent */
  ${({ hoverPosition, isOverCurrent, theme, isParentComponent }) => {
    if (isParentComponent) return '';
    const hoverColor = theme.color.primary.brand500;
    let hoverCSS = 'position: relative;';

    if (isOverCurrent) {
      switch (hoverPosition) {
        case 'top':
          return (hoverCSS += `box-shadow: 0px -2px 0px ${hoverColor}};`);
        case 'bottom':
          return (hoverCSS += `box-shadow: 0px 2px 0px ${hoverColor}};`);
        case 'left':
          return (hoverCSS += `box-shadow: -2px 0px 0px ${hoverColor}};`);
        case 'right':
          return (hoverCSS += `box-shadow: 2px 0px 0px ${hoverColor}};`);
        default:
          return;
      }
    }

    return '';
  }}

  ${({ isHovering, isOverCurrent, hoverPosition }) => css`
    ${((isOverCurrent && Boolean(hoverPosition)) || isHovering) && 'z-index: 2;'}
  `}


  ${({ isHovering, isOver, isParentComponent, theme }) =>
    (isHovering || (isOver && isParentComponent)) &&
    css`
      outline-style: solid;
      outline-color: ${theme.color.primary.brand200};
    `};

  // highlights the current parent component when it's hovered
  ${({ isOver, isParentComponent, isOverCurrent, theme }) => {
    if (isOverCurrent && isOver && isParentComponent) {
      return css`
        outline-style: solid;
        outline-color: ${theme.color.primary.brand700};
      `;
    }
  }}};

  ${({ isSelected, isErrorSelected, theme }) =>
    (isSelected || isErrorSelected) &&
    css`
      outline-color: ${isErrorSelected ? theme.colorError : theme.color.primary.brand700};
      outline-style: solid;
      z-index: 2;
    `}

  ${({ component }) =>
    component.type === UiComponentType.Footer &&
    css`
      & > div:first-child {
        display: none;
      }

      & > div {
        position: relative;
      }

      position: relative;
    `}

  a,
  input,
  div:is([aria-checked]),
  select {
    pointer-events: none;
  }

  &:is([data-component-type='horizontal_stack'][data-child-count='0']),
  &:is([data-component-type='horizontal_stack'][data-child-count='0']) > div,
  &:is([data-component-type='footer']) > div:last-child {
    min-height: 58px;
  }

  // apply padding to allow space to select children when:
  // 1) The horizontal stack component is hovered or
  // 2) It's children components are hovered or selected
  &:is([data-component-type='horizontal_stack']):not([data-child-count='0']) {
    &:has(:is([data-selected="true"])),
    &:is([data-selected='true']) {
      box-sizing: border-box;
      & > div:first-child {
        padding-top: ${({ component }) =>
          determineStackContainerSidePadding(
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            (component as HorizontalStackComponent)?.styles?.padding?.base?.base?.top,
          )};
        padding-bottom: ${({ component }) =>
          determineStackContainerSidePadding(
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            (component as HorizontalStackComponent)?.styles?.padding?.base?.base?.bottom,
          )};
        padding-left: ${({ component }) =>
          determineStackContainerSidePadding(
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            (component as HorizontalStackComponent)?.styles?.padding?.base?.base?.left,
          )};
        padding-right: ${({ component }) =>
          determineStackContainerSidePadding(
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            (component as HorizontalStackComponent)?.styles?.padding?.base?.base?.right,
          )};
      }
    }
  }

  // hovering over a nested component when the horizontal stack is currently selected
  &:is([data-component-type='horizontal_stack']):not([data-child-count='0']):has(:is([data-selected="true"])) {
    outline-color: ${({ theme, isSelected }) =>
      isSelected ? `${theme.color.primary.brand700}` : `${theme.color.primary.brand200}`};
    outline-style: dashed;
  }

  // default unselected state
  &:is([data-component-type='horizontal_stack'][data-child-count='0']):not([data-selected="true"]) {
    outline: none;
    border: 1px dashed;
  }

  // selected state for horizontal component with children
  &:is([data-component-type='horizontal_stack']):not([data-child-count='0']):is([data-selected="true"]) {
    outline-style: solid;
  }

  /* make underlying components uninteractive */
  ${({ isParentComponent }) =>
    !isParentComponent
      ? css`
          & > * {
            z-index: -1;
          }
        `
      : css`
          * > button:not(#clickable__stack) {
            pointer-events: none;
          }
        `}
  // Create a clickable overlay so that buttons are still accessible by click.
  // The browser forces no user interaction due to the disabled attrbute
  ${({ component }) =>
    isUiButtonComponent(component) &&
    isDisabledComponent(component) &&
    css`
      &::before {
        display: block;
        width: 100%;
        height: 100%;
        content: '';
        position: absolute;
        top: 0px;
        left: 0px;
        z-index: 1;
        background: transparent;
      }
    `}

    ${baseContentEditableStyles}
`;

const CenteredContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  height: 14px;
  max-height: 14px;
`;

const StyledEyeOffIcon = styled(EyeOffIcon)`
  width: 12px;
  height: 12px;
  margin-right: ${Spacing[1]};
`;

const ClippedText = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 130px;
`;

const shouldDisableDrop = (item: ScreenEditorComponentItem, componentConfig: UiComponentConfig) => {
  // these are components generated from the component-schema when a user drags a component onto the screen
  // from the Component Library
  if (!('name' in item)) return false;

  // These type of component serialize stacks, but should not accept new children, as the children in the
  // client preview are dependent on config updates (other than adding/removing child components)
  if (isComposedUiComponentConfig(componentConfig)) return true;

  return (
    item?.name === componentConfig.name ||
    (isUiParentComponentConfig(item as UiComponentConfig) &&
      (item as UiParentComponentConfig).config.children.includes(componentConfig))
  );
};

type ScreenComponentProps = {
  /**
   * Only used for children of virtual components that don't exist in config to determine stack sizing.
   */
  index: number;
  component: UiComponent;
  selectedComponentAndStep: SelectedComponentAndStep;
  mockInquiry: MockInquiryPropsForUiComponent;
  step: InquiryTemplateVersionStepUi;
  stepPreview: StepPreview;
  fieldSchema: FieldSchema[];
  initialFieldSchema: FieldSchema[];
  hoveredIndex: string[];
  hoverPosition: DraggableHoverPosition;
  parentComponent?: UiParentComponent;
  canNestComponents?: boolean;
  editable: boolean;
  handleOnClick: (value: SelectedComponentAndStep) => void;
  setHoveredIndex: (value: string[] | null) => void;
  setHoverPosition: (position: DraggableHoverPosition) => void;
  updateSteps: ({
    updatedStep,
    component,
    fields,
  }: {
    updatedStep: InquiryTemplateVersionStep;
    component?: UiComponentConfig;
    fields?: FieldSchema[];
  }) => void;
};

export const ScreenComponent = ({
  index,
  component,
  selectedComponentAndStep,
  mockInquiry,
  step,
  stepPreview,
  fieldSchema,
  initialFieldSchema,
  hoveredIndex,
  hoverPosition,
  parentComponent,
  canNestComponents,
  editable,
  handleOnClick,
  setHoveredIndex,
  setHoverPosition,
  updateSteps,
}: ScreenComponentProps) => {
  const [isHovering, { enable: setIsHovering, disable: setStopHovering }] = useToggle(false);
  const [mouseDirection, setMouseDirection] = useState<'down' | 'up' | null>(null);
  const [childDropPosition, setChildDropPosition] = useState<'out' | 'in' | null>(null);

  const finalRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const useInlineTextEditor = useFeatureFlag('dashboard:fle-enable-inline-text-editing');

  // TODO: [@thwang] Virtual components, e.g. components that are created by
  // other components in as_client_data, won't be present in
  // step.config.components. In the future, we should have some kind of flag
  // that FLE can use to treat virtual components as immutable, undraggable,
  // undroppoable, etc. For now, we assume that if component config is missing,
  // the component is virtual. Currently, the only component that uses virtual
  // components is Stepper, which creates stacks, images, and text.
  const componentPath: string[] | undefined = findComponentConfigPath(
    (componentConfig) => componentConfig.name === component.name,
    step.config.components,
  );
  const isVirtualComponent = componentPath == null;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const componentConfig: UiComponentConfig | undefined = get(componentPath, step.config.components);

  const canDrag = useMemo(() => {
    return (
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      !NONDRAGGABLE_COMPONENTS.includes(componentConfig?.type) && editable && !isVirtualComponent
    );
  }, [componentConfig?.type, editable, isVirtualComponent]);

  const [_, dragRef, dragPreview] = useDrag(
    () => ({
      type: ScreenEditorDraggableItemTypes.COMPONENT,
      item: {
        ...componentConfig,
        stepName: step.name,
      },
      canDrag,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [component],
  );

  const resetDrop = () => {
    setHoverPosition(null);
    setMouseDirection(null);
    setStopHovering();
  };

  const [{ isOver, isOverCurrent, componentHoverIndex }, dropRef] = useDrop<
    ScreenEditorComponentItem,
    void,
    { isOver: boolean; isOverCurrent: boolean; componentHoverIndex: Array<string> | undefined }
  >(
    () => ({
      canDrop: (item: ScreenEditorComponentItem) => {
        return (
          (!isDraggedComponentFooter(item?.type) &&
            canNestComponents &&
            editable &&
            !isVirtualComponent) ??
          false
        );
      },
      accept: ScreenEditorDraggableItemTypes.COMPONENT,
      hover: (item: ScreenEditorComponentItem, monitor) => {
        const determineIndicatorPosition = (parentIsHorizontalComponent: boolean) => {
          const hoverBoundingRect = finalRef.current?.getBoundingClientRect();
          const clientOffset = monitor.getClientOffset();
          const dragOffset = monitor.getDifferenceFromInitialOffset();
          if (hoverBoundingRect && clientOffset && dragOffset) {
            const componentHeight = hoverBoundingRect.bottom - hoverBoundingRect.top;
            const componentWidth = hoverBoundingRect.right - hoverBoundingRect.left;
            // Get vertical middle
            const hoverMiddleY = componentHeight / 2;
            const hoverMiddleX = componentWidth / 2;

            // Determine mouse position
            // Get pixels to the top
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;
            // Get pixels to the right
            const hoverClientX = clientOffset.x - hoverBoundingRect.left;

            const CHILD_DROP_OUT_TOLERANCE = 5;

            if (!parentIsHorizontalComponent) {
              // Dragging upwards
              setMouseDirection(dragOffset.y > 0 ? 'down' : 'up');

              if (
                hoverClientY <= CHILD_DROP_OUT_TOLERANCE ||
                hoverClientY >= componentHeight - CHILD_DROP_OUT_TOLERANCE
              ) {
                setChildDropPosition('out');
              } else {
                setChildDropPosition('in');
              }

              if (hoverClientY < hoverMiddleY) {
                setHoverPosition('top');
              }

              // Dragging downwards
              if (hoverClientY > hoverMiddleY) {
                setHoverPosition('bottom');
              }
            } else {
              setMouseDirection(dragOffset.x < 0 ? 'down' : 'up');

              if (
                hoverClientY <= CHILD_DROP_OUT_TOLERANCE ||
                hoverClientX >= componentWidth - CHILD_DROP_OUT_TOLERANCE
              ) {
                setChildDropPosition('out');
              } else {
                setChildDropPosition('in');
              }
              // Dragging left
              if (hoverClientX < hoverMiddleX) {
                setHoverPosition('left');
              }

              // Dragging right
              if (hoverClientX > hoverMiddleX) {
                setHoverPosition('right');
              }
            }
          }
        };

        // we only want to fire this if the current component is hovered
        if (!isOverCurrent) return;

        const hasHorizontalParent =
          parentComponent != null ? componentFlexToHorizontalAxis(parentComponent) : false;

        // don't do anything if we're hovering over the current
        // dragged item
        if (componentConfig && (isVirtualComponent || shouldDisableDrop(item, componentConfig))) {
          setHoverPosition(null);
          return;
        }

        determineIndicatorPosition(hasHorizontalParent);
      },
      collect: (monitor) => ({
        isOverCurrent:
          monitor.isOver({
            shallow: true,
          }) && !isDraggedComponentFooter((monitor.getItem() as ScreenEditorComponentItem).type),
        isOver:
          monitor.isOver() &&
          !isDraggedComponentFooter((monitor.getItem() as ScreenEditorComponentItem).type),
        componentHoverIndex: findComponentConfigPath(
          (componentConfig) => componentConfig.name === component.name,
          step.config.components,
        ),
      }),
      drop: (item: ScreenEditorComponentItem, monitor) => {
        const didDrop = monitor.didDrop();
        // checking to see if the drop happened within the nested drop target
        // or a component that can accept children
        if (didDrop) {
          resetDrop();
          return;
        }

        // limit copying over components from different screens
        if (!isComponentFromStep(item, step)) {
          resetDrop();
          dispatch(
            showMessage('Cannot copy components from different screens', MessageType.Failure),
          );
          return;
        }

        // limit dropping certain components within others
        if (componentConfig && restrictNestedComponentDrop({ item, componentConfig })) {
          resetDrop();
          dispatch(
            showMessage(
              `Cannot drop ${getComponentDisplayName(
                item,
              )} component within ${getComponentDisplayName(componentConfig)}`,
              MessageType.Failure,
            ),
          );
          return;
        }

        // Make it so that we can't drop components on themselves, or consider child components droppable
        if (item?.name === componentConfig?.name) {
          return;
        }

        // check to see if the component already exists within the parent component
        // and the position has changed
        // if not, do nothing
        const originalComponentPath = findComponentConfigPath(
          (component: UiComponentConfig) => component.name === item.name,
          step.config.components,
        );

        // disallow moving BIPA components
        if (
          originalComponentPath != null &&
          (isCurrentComponentBIPASubmitTrigger({ step, component: item as UiComponentConfig }) ||
            isCurrentComponentBIPAText({ step, component: item as UiComponentConfig }))
        ) {
          dispatch(
            showMessage('Cannot move component due to BIPA compliance', MessageType.Failure),
          );
          return;
        }

        if (hoveredIndex) {
          const firstHoveredIndex = hoveredIndex.at(0);
          const hoveredStepComponentPath = hoveredIndex.slice(0, -3);
          const lastHoveredIndex = hoveredIndex.at(-1);
          if (lastHoveredIndex) {
            let lastIndexHovered = parseInt(lastHoveredIndex);

            let updatedStep: InquiryTemplateVersionStepUi = step;
            let newComponentConfig: UiComponentConfig | null = null;
            let newComponentFields: FieldSchema[] = [];

            const isMovingComponentUp = isShiftingComponentUp(hoverPosition);

            // TODO/FAST-FOLLOW (Evan): Once parent components PR is merged,
            // immediately address the duplication with the logic below and
            // DRY up code for less confusion

            // if this a component already on stepPreview
            if (originalComponentPath != null) {
              const lastOriginalComponentPath = originalComponentPath.at(-1);
              if (lastOriginalComponentPath) {
                const originalComponentConfig = omit('stepName', item) as UiComponentConfig;
                const originalComponentIndex = parseInt(lastOriginalComponentPath);

                let updateIndex = 0;

                if (hoveredIndex.length > 1 && originalComponentPath.length > 1) {
                  const hoveredComponentConfig = get(hoveredIndex, updatedStep.config.components);
                  const hoveredParentComponentConfig = get(
                    hoveredStepComponentPath,
                    updatedStep.config.components,
                  );

                  const hoveredIsParentComponent =
                    isUiParentComponentConfig(hoveredComponentConfig);
                  const hoveredHasParentComponent = isUiParentComponentConfig(
                    hoveredParentComponentConfig,
                  );

                  const originalItemParentComponentConfig = get(
                    originalComponentPath.slice(0, -3),
                    updatedStep.config.components,
                  );
                  const originalHasParentComponent =
                    originalComponentPath.length > 1 &&
                    isUiParentComponentConfig(originalItemParentComponentConfig);

                  if (hoveredIsParentComponent) {
                    // moving from one parent to another
                    updateIndex = determineComponentShiftIndex({
                      hoverComponentIndex: isMovingComponentUp
                        ? 0
                        : hoveredComponentConfig.config.children.length,
                      hoverPosition,
                    });

                    updatedStep = removeComponentFromPath(originalComponentConfig, updatedStep);

                    // get the new hover index because we've just removed a component
                    const updatedHoverIndex = findComponentConfigPath(
                      (component) => component.name === hoveredComponentConfig.name,
                      updatedStep.config.components,
                    );

                    if (updatedHoverIndex) {
                      const updatedHoveredComponentConfig = get(
                        updatedHoverIndex,
                        updatedStep.config.components,
                      );

                      updatedStep = addComponentsAtIndex({
                        index: updateIndex,
                        componentConfigs: [originalComponentConfig],
                        configArray: updatedHoveredComponentConfig.config.children,
                        path: [...updatedHoverIndex, 'config', 'children'],
                        step: updatedStep,
                      });
                    }
                    // get the updated hover component config
                  } else {
                    // shifting inside the parent
                    if (
                      !isUiParentComponentConfig(hoveredComponentConfig) &&
                      !isUiParentComponentConfig(originalComponentConfig) &&
                      hoveredHasParentComponent
                    ) {
                      // moving from one parent into another, and the hovered component
                      // is nested in a parent component
                      updatedStep = removeComponentFromPath(originalComponentConfig, updatedStep);

                      const updatedHoveredComponentPathParent = findComponentConfigPath(
                        (component: UiComponentConfig) =>
                          component.name === hoveredParentComponentConfig.name,
                        updatedStep.config.components,
                      );

                      if (updatedHoveredComponentPathParent) {
                        const parentPathComponents = get(
                          updatedHoveredComponentPathParent,
                          updatedStep.config.components,
                        );

                        updatedStep = addComponentsAtIndex({
                          index: determineComponentShiftIndex({
                            // only want to pass originalComponentIndex if we're shifting in the same parent component
                            originalComponentIndex:
                              originalHasParentComponent &&
                              hoveredParentComponentConfig.name ===
                                originalItemParentComponentConfig.name
                                ? originalComponentIndex
                                : null,
                            hoverComponentIndex: lastIndexHovered,
                            hoverPosition,
                          }),
                          componentConfigs: [originalComponentConfig],
                          configArray: parentPathComponents.config.children,
                          path: [...updatedHoveredComponentPathParent, 'config', 'children'],
                          step: updatedStep,
                        });
                      }
                    } else {
                      const initialPath = hoveredHasParentComponent
                        ? [...hoveredStepComponentPath, 'config', 'children']
                        : hoveredIndex.slice(0, -1);

                      const parentPathComponents = get(
                        hoveredStepComponentPath,
                        updatedStep.config.components,
                      );

                      updatedStep = removeComponentFromPath(originalComponentConfig, updatedStep);

                      if (firstHoveredIndex) {
                        updateIndex = determineComponentShiftIndex({
                          originalComponentIndex,
                          hoverComponentIndex: hoveredHasParentComponent
                            ? lastIndexHovered
                            : parseInt(firstHoveredIndex),
                          hoverPosition,
                        });

                        const updatedHoveredParentComponentConfig = get(
                          hoveredStepComponentPath,
                          updatedStep.config.components,
                        );

                        const configArray = hoveredHasParentComponent
                          ? updatedHoveredParentComponentConfig.config.children
                          : parentPathComponents.config.children;

                        updatedStep = addComponentsAtIndex({
                          index: updateIndex,
                          componentConfigs: [originalComponentConfig],
                          configArray: configArray,
                          path: initialPath,
                          step: updatedStep,
                        });
                      }
                    }
                  }
                } else {
                  if (
                    (originalComponentPath.length === 1 && hoveredIndex.length === 1) ||
                    (hoveredIndex.length > 1 && originalComponentPath.length === 1)
                  ) {
                    const isHoveredParentComponent = isUiParentComponentConfig(
                      get(hoveredIndex, updatedStep.config.components),
                    );
                    // moving inside parent component
                    //case 1: we're hovering over a parent component and should insert into it
                    //case 2: we're hovering over a nested component and should insert inbetween
                    const parentConfigPath =
                      hoveredIndex.length > 1
                        ? isHoveredParentComponent
                          ? hoveredIndex
                          : hoveredIndex.slice(0, -3)
                        : hoveredIndex;
                    const parentConfig = get(parentConfigPath, updatedStep.config.components);

                    updatedStep = addComponentsAtIndex({
                      index: determineComponentShiftIndex({
                        originalComponentIndex: isHoveredParentComponent
                          ? null
                          : originalComponentIndex,
                        hoverComponentIndex: isHoveredParentComponent
                          ? isMovingComponentUp
                            ? 0
                            : parentConfig.config.children.length
                          : lastIndexHovered,
                        hoverPosition,
                      }),
                      path: [...parentConfigPath, 'config', 'children'],
                      configArray: parentConfig.config.children,
                      componentConfigs: [originalComponentConfig],
                      step: updatedStep,
                    });
                    updatedStep = removeComponentFromPath(originalComponentConfig, updatedStep);
                  } else {
                    // moving to outside of parent component
                    const hoveredComponentConfig = get(hoveredIndex, updatedStep.config.components);
                    const hoveredIsParentComponent =
                      isUiParentComponentConfig(hoveredComponentConfig);
                    updatedStep = removeComponentFromPath(originalComponentConfig, updatedStep);

                    if (hoveredIsParentComponent) {
                      // moving into another parent
                      // get the new hover index because we've just removed a component
                      const updatedHoverIndex = findComponentConfigPath(
                        (component) => component.name === hoveredComponentConfig.name,
                        updatedStep.config.components,
                      );

                      if (updatedHoverIndex) {
                        // get the updated hover component config
                        const updatedHoveredComponentConfig = get(
                          updatedHoverIndex,
                          updatedStep.config.components,
                        );

                        // get original parent component index
                        // moving down: parent index + 1 or bottom
                        // moving up: parent index - 1 or zero
                        const originalComponentConfigPath = originalComponentPath.slice(0, -3);

                        const lastOriginalComponentConfigPath = originalComponentConfigPath.at(-1);

                        if (lastOriginalComponentConfigPath) {
                          const originalComponentConfigPathIndex = parseInt(
                            lastOriginalComponentConfigPath,
                          );

                          const originalItemParentComponentConfig = get(
                            originalComponentConfigPath,
                            updatedStep.config.components,
                          );

                          const isMovingMouseOrComponentUp =
                            isMovingComponentUp || mouseDirection === 'up';
                          let dropConfigArray = updatedHoveredComponentConfig.config.children;
                          let dropPath: Array<string> | null = [
                            ...updatedHoverIndex,
                            'config',
                            'children',
                          ];

                          if (
                            originalItemParentComponentConfig.name === hoveredComponentConfig.name
                          ) {
                            const hoverComponentIndex = isMovingMouseOrComponentUp
                              ? Math.max(originalComponentConfigPathIndex - 1, 0)
                              : Math.min(
                                  originalComponentConfigPathIndex + 1,
                                  updatedHoveredComponentConfig.config.children.length,
                                );

                            let updateIndex = determineComponentShiftIndex({
                              hoverComponentIndex,
                              hoverPosition,
                            });

                            // tolerance hit, so we want to stack the component rather than move it into any component
                            if (childDropPosition === 'out') {
                              const dropHoverPath =
                                updatedHoverIndex.length > 1
                                  ? updatedHoverIndex.slice(0, -3)
                                  : updatedHoverIndex;

                              if (dropHoverPath.length === 1) {
                                dropPath = null;
                                dropConfigArray = updatedStep.config.components;
                                const lastDropHoverPath = dropHoverPath.at(-1);
                                if (lastDropHoverPath) {
                                  const dropIndex = parseInt(lastDropHoverPath);
                                  updateIndex = isMovingMouseOrComponentUp
                                    ? Math.max(dropIndex, 0)
                                    : Math.min(dropIndex + 1, updatedStep.config.components.length);
                                }
                              }
                            }

                            updatedStep = addComponentsAtIndex({
                              index: updateIndex,
                              path: dropPath,
                              configArray: dropConfigArray,
                              componentConfigs: [originalComponentConfig],
                              step: updatedStep,
                            });
                          } else {
                            const hoverComponentIndex = isMovingMouseOrComponentUp
                              ? Math.max(originalComponentConfigPathIndex, 0)
                              : Math.min(
                                  originalComponentConfigPathIndex + 1,
                                  updatedHoveredComponentConfig.config.children.length,
                                );

                            let updateIndex = determineComponentShiftIndex({
                              hoverComponentIndex,
                              hoverPosition,
                            });

                            // tolerance hit, so we want to stack the component rather than move it into any component
                            if (childDropPosition === 'out') {
                              const dropHoverPath =
                                updatedHoverIndex.length > 1
                                  ? updatedHoverIndex.slice(0, -3)
                                  : updatedHoverIndex;

                              if (dropHoverPath.length === 1) {
                                dropPath = null;
                                dropConfigArray = updatedStep.config.components;
                                const lastDropHoverPath = dropHoverPath.at(-1);
                                if (lastDropHoverPath) {
                                  const dropIndex = parseInt(lastDropHoverPath);
                                  updateIndex = isMovingMouseOrComponentUp
                                    ? Math.max(dropIndex, 0)
                                    : Math.min(dropIndex + 1, updatedStep.config.components.length);
                                }
                              }
                            }
                            updatedStep = addComponentsAtIndex({
                              index: updateIndex,
                              path: dropPath,
                              configArray: dropConfigArray,
                              componentConfigs: [originalComponentConfig],
                              step: updatedStep,
                            });
                          }
                        }
                      }
                    } else {
                      if (firstHoveredIndex) {
                        lastIndexHovered = parseInt(firstHoveredIndex);
                        updatedStep = addComponentsAtIndex({
                          index: determineComponentShiftIndex({
                            hoverComponentIndex: lastIndexHovered,
                            hoverPosition,
                          }),
                          configArray: updatedStep.config.components,
                          componentConfigs: [originalComponentConfig],
                          step: updatedStep,
                        });
                      }
                    }
                  }
                }
              }
            } else {
              const { componentConfig, componentFields } = createComponentConfigAndFields({
                itemConfig: item,
                // both initial and form value fieldSchemas are used to avoid
                // collisions with field keys which may be deleted prior to save.
                fieldKeys: uniq([...fieldSchema, ...initialFieldSchema].map(({ key }) => key)),
                step,
              });
              newComponentConfig = componentConfig;
              newComponentFields = componentFields;

              if (firstHoveredIndex) {
                const hoverIndexAbove = (parseInt(firstHoveredIndex) - 1).toString();
                const componentAbove = get([hoverIndexAbove], step.config.components);

                const component = firstHoveredIndex
                  ? (get([firstHoveredIndex], step.config.components) as UiComponentConfig)
                  : null;

                if (component) {
                  // edge case of stacked parent components
                  const hoveringAdjacentParentComponents =
                    isMovingComponentUp &&
                    isUiParentComponentConfig(componentAbove) &&
                    isUiParentComponentConfig(component);

                  if (
                    // we exclude footer because we always want to drop into the footer rather than
                    // figure out the proper index
                    get(hoveredIndex, step.config.components).type !==
                      UiComponentConfigType.Footer &&
                    hoveringAdjacentParentComponents
                  ) {
                    // insert above if possible
                    updatedStep = addComponentsAtIndex({
                      index: determineComponentShiftIndex({
                        hoverComponentIndex: lastIndexHovered,
                        hoverPosition,
                      }),
                      configArray: updatedStep.config.components,
                      componentConfigs: [newComponentConfig],
                      step: updatedStep,
                    });
                  } else {
                    //case 1: we're hoving over a parent component and should insert into it
                    //case 2: we're hovering over a nested component and should insert inbetween

                    const isHoveredParentComponent = isUiParentComponentConfig(
                      get(hoveredIndex, updatedStep.config.components),
                    );

                    const parentConfigPath =
                      hoveredIndex.length > 1
                        ? isHoveredParentComponent
                          ? hoveredIndex
                          : hoveredIndex.slice(0, -3)
                        : hoveredIndex;

                    const parentPathComponents = get(parentConfigPath, step.config.components);

                    const updateIndex = isHoveredParentComponent
                      ? determineComponentShiftIndex({
                          hoverComponentIndex: isMovingComponentUp
                            ? 0
                            : parentPathComponents.config.children.length,
                          hoverPosition,
                        })
                      : determineComponentShiftIndex({
                          hoverComponentIndex: lastIndexHovered,
                          hoverPosition,
                        });

                    updatedStep = addComponentsAtIndex({
                      index: updateIndex,
                      path:
                        hoveredIndex.length > 1
                          ? isHoveredParentComponent
                            ? [...hoveredIndex, 'config', 'children']
                            : hoveredIndex.slice(0, -1)
                          : [...hoveredIndex, 'config', 'children'],
                      configArray: parentPathComponents.config.children,
                      componentConfigs: [newComponentConfig],
                      step: updatedStep,
                    });
                    // insert component preview
                    const updatedComponentConfigPath = findComponentConfigPath(
                      (component: UiComponentConfig) => component.name === newComponentConfig?.name,
                      updatedStep.config.components,
                    );
                    if (updatedComponentConfigPath) {
                      dispatch(
                        refreshComponentPreview(
                          updatedStep.name,
                          updatedComponentConfigPath.join('.'),
                          newComponentConfig,
                        ),
                      );
                    }
                  }
                }
              }
            }

            updateSteps({
              updatedStep,
              ...(newComponentConfig ? { component: newComponentConfig } : {}),
              fields: fieldSchema.concat(newComponentFields),
            });
            resetDrop();
          }
        }
      },
    }),
    [hoveredIndex, step, updateSteps, hoverPosition, mouseDirection, childDropPosition],
  );

  useEffect(() => {
    if (componentHoverIndex) {
      if (isOver && isOverCurrent) {
        setHoveredIndex(componentHoverIndex);
      }
    }
  }, [isOver, isOverCurrent, setHoveredIndex, componentHoverIndex, dragPreview]);

  dragRef(dropRef(finalRef));

  //disable default drag preview
  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  const onClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.nativeEvent.stopImmediatePropagation();
      event.stopPropagation();
      const selectedComponentPath = findComponentConfigPath(
        (stepComponent) => stepComponent.name === component.name,
        step.config.components,
      );
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const selectedComponent = get(selectedComponentPath, step.config.components);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleOnClick({ component: selectedComponent, step, error: false });
    },
    [step, handleOnClick, component.name],
  );

  const previewComponent = produce(component, (draftComponent: UiComponent) => {
    // Screen editor will use an indicator that this component in context of screen editor
    // will been visilble, but in other areas of FLE and inquiries will not be rendered
    if (isHideableComponent(component)) {
      (draftComponent as UiHideableComponent).attributes.hidden = false;
    }

    if (isDisableableComponent(draftComponent)) {
      draftComponent.attributes.disabled = isDisabledComponentConfig(componentConfig);
    }

    return draftComponent;
  });

  const componentStickerContent = (
    <div style={{ display: 'flex' }}>
      <CenteredContainer>
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        {isHiddenComponentConfig(componentConfig) && <StyledEyeOffIcon />}
        <ClippedText>{component.name}</ClippedText>
      </CenteredContainer>
    </div>
  );

  const renderedComponent = (
    <UiComponentView
      key={component.name}
      component={previewComponent}
      {...mockInquiry}
      {...(canNestComponents && {
        isPreview: true,
        childPreviews: (component as UiParentComponent).attributes.children.map(
          (subcomponent: UiComponent, idx: number) => {
            return {
              name: subcomponent.name,
              component: (
                <ScreenComponent
                  key={`${subcomponent.name}-${idx}`}
                  index={idx}
                  component={subcomponent}
                  stepPreview={stepPreview}
                  hoveredIndex={hoveredIndex}
                  setHoveredIndex={setHoveredIndex}
                  hoverPosition={hoverPosition}
                  setHoverPosition={setHoverPosition}
                  step={step}
                  handleOnClick={handleOnClick}
                  selectedComponentAndStep={selectedComponentAndStep}
                  fieldSchema={fieldSchema}
                  initialFieldSchema={initialFieldSchema}
                  updateSteps={updateSteps}
                  canNestComponents={isUiParentComponent(subcomponent)}
                  parentComponent={component as UiParentComponent}
                  editable={editable && !isVirtualComponent}
                  mockInquiry={mockInquiry}
                />
              ),
            };
          },
        ),
      })}
    />
  );

  // Wrap children of HorizontalStack and ClickableStack in <StackBase> to mimic
  // parent component styles
  const renderStackChildWrapper =
    parentComponent != null &&
    [UiComponentType.HorizontalStack, UiComponentType.ClickableStack].includes(
      parentComponent.type,
    );

  const isCurrentSelectedComponent =
    selectedComponentAndStep?.component?.name === component.name &&
    selectedComponentAndStep?.step?.name === step.name;

  const currentLocale = useSelector<RootState, InquiryIso6391LanguageCode | undefined>(
    (state) => state.inquiryTemplate.locale,
  );

  const onTranslationUpdate = useCallback(
    (text: string, customTranslationsKey: string) => {
      if (isTextComponentConfig(componentConfig) && componentPath != null) {
        const translations =
          componentConfig.config[customTranslationsKey as keyof UiComponentConfig['config']] || [];

        const updatedStep = generateUpdatedStepFromComponentUpdates(step, componentPath, {
          [customTranslationsKey]: handleTranslationUpdate({
            translations,
            value: text,
            locale: currentLocale || 'en-US',
          }),
        });

        updateSteps({ updatedStep });
      }
    },
    [currentLocale, componentConfig, componentPath, updateSteps, step],
  );

  const onScreenComponent = isImageComponent(component) ? (
    /** Ensures that live preview alignment is correct */
    <ImageWrapper
      alignment={component.styles?.justify?.base?.base}
      width={sizeCss(component.styles?.width?.base?.base)}
    >
      <StalledAnimationContainer playAnimation={isCurrentSelectedComponent}>
        {renderedComponent}
      </StalledAnimationContainer>
    </ImageWrapper>
  ) : (
    <>
      {shouldEnableInlineTextEdits({
        stepConfig: step,
        componentPreview: component,
        componentConfig: componentConfig,
        useInlineTextEditor: useInlineTextEditor,
      }) ? (
        <InlineTextEditor
          componentConfig={componentConfig as UiTextComponentConfig}
          componentPreview={component as UiTextComponent}
          wrapperElement={finalRef?.current}
          editable={editable}
          onUpdate={onTranslationUpdate}
        >
          {renderedComponent}
        </InlineTextEditor>
      ) : (
        renderedComponent
      )}
    </>
  );

  const InteractiveScreenComponent = (
    <ScreenComponentWrapper
      ref={finalRef}
      id={`stepPreview-component-wrapper__${step.name}-${component.name}`}
      data-component-type={component.type}
      data-selected={isHovering || isCurrentSelectedComponent || isOver}
      component={component}
      parentComponent={parentComponent}
      hoverPosition={hoverPosition}
      isErrorSelected={isCurrentSelectedComponent && selectedComponentAndStep?.error}
      isHovering={isHovering}
      isOver={isOver}
      isOverCurrent={isOverCurrent}
      isParentComponent={canNestComponents}
      isSelected={isCurrentSelectedComponent}
      onClick={isVirtualComponent ? undefined : onClick}
      onMouseOut={
        isVirtualComponent
          ? undefined
          : (e: React.MouseEvent<HTMLDivElement>) => {
              e.stopPropagation();
              setStopHovering();
            }
      }
      onMouseOver={
        isVirtualComponent
          ? undefined
          : (e: React.MouseEvent<HTMLDivElement>) => {
              e.stopPropagation();
              setIsHovering();
            }
      }
      {...(canNestComponents && {
        'data-child-count': (component as UiParentComponent).attributes.children.length,
        parentFramePositioning: (component as ParentLayoutComponent).styles?.['frame-positioning']
          ?.base?.base,
      })}
    >
      {(isHovering || isCurrentSelectedComponent) && (
        <div style={{ position: 'absolute', top: '-28px' }}>
          <NodeSticker title={componentStickerContent} isActive selected />
        </div>
      )}
      {onScreenComponent}
    </ScreenComponentWrapper>
  );

  return (
    <>
      {renderStackChildWrapper && (
        <StackChild
          styles={parentComponent?.styles as StackComponentStyles}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          index={isVirtualComponent ? index : parseInt(componentPath.at(-1))}
        >
          {InteractiveScreenComponent}
        </StackChild>
      )}
      {!renderStackChildWrapper && InteractiveScreenComponent}
    </>
  );
};
ScreenComponent.displayName = 'ScreenComponent';
